import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { api } from '../../lib/api';
import { buildHealthPage, type AttentionRow } from '../../lib/sourceHealth/page';
import type { SourceHealth } from '../../types/sourceHealth';

// THE COVERAGE WORKLIST — "what needs me right now?"
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md §4, D9e step 5
//
// ⚠️⚠️ THIS IS A WORKLIST, NOT A DASHBOARD. No charts, no sparklines, no vanity
// counts. Red and amber at the top with what to do about them; everything else
// one collapsed green line per source. Four sources' worth of correctly-modelled
// health is the INPUT — the delivery/activity split, jobs vs accounts, causes vs
// symptoms — and none of that structure belongs on screen.
//
// ⚠️ EVERY DECISION IS UPSTREAM in `src/lib/sourceHealth/` so it can be tested
// without jsdom (the `pullFreshnessMessage` precedent, and this suite sits close
// to its per-test timeout). This file renders and nothing else.

interface HealthPayload {
  sources: SourceHealth[];
  failures: Array<{ source: string; error: string }>;
}

const DOT: Record<string, string> = {
  red: 'bg-rose-500',
  amber: 'bg-amber-500',
  green: 'bg-kyck-500',
};

function Row({ row }: { row: AttentionRow }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-border last:border-0 py-3">
      <div className="flex items-start gap-3">
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[row.status] ?? 'bg-muted'}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-foreground">{row.title}</span>
            <span className="text-xs text-muted-foreground">{row.source}</span>
            {/* ⚠️ Shown only when it means something. "1 account" on a plain row
                is noise, and the row IS the account. */}
            {row.affectedCount > 1 && (
              <span className="text-xs text-muted-foreground">
                {row.affectedCount} accounts
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{row.reason}</p>
          <p className="text-sm">
            {row.action}{' '}
            {row.href && (
              <a className="text-kyck-600 underline underline-offset-2" href={row.href}>
                Open
              </a>
            )}
          </p>
          {row.affected.length > 0 && (
            <div className="mt-1">
              <button
                type="button"
                className="text-xs text-muted-foreground underline underline-offset-2"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? 'Hide' : 'Show'} the {row.affected.length} accounts
              </button>
              {open && (
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {row.affected.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export function SourceHealthPage(): React.ReactElement {
  const query = useQuery({
    queryKey: ['source-health'],
    queryFn: () => api<HealthPayload>('/api/source-health'),
  });

  const model = useMemo(
    () => (query.data ? buildHealthPage(query.data.sources, Date.now()) : null),
    [query.data],
  );

  return (
    <>
      <PageHeader
        title="Source health"
        description="What needs attention right now, across Meta, Google Ads and the CRM."
      />

      {query.isLoading && <Spinner />}
      {query.error && (
        <p className="text-sm text-rose-600">Could not load source health.</p>
      )}

      {/* ⚠️⚠️ A FAILED SOURCE IS STATED, NEVER OMITTED. A page that silently
          dropped Google Ads would show a SHORTER worklist and read as better
          news — the exact inversion this page exists to prevent. */}
      {query.data && query.data.failures.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          {query.data.failures.map((f) => (
            <p key={f.source}>
              <strong>{f.source}</strong> could not be checked, so nothing below covers
              it: {f.error}
            </p>
          ))}
        </div>
      )}

      {model && (
        <div className="space-y-8">
          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">Needs attention</h2>
            {model.allClear ? (
              // ⚠️ SAY SO PLAINLY rather than rendering an empty container.
              <p className="text-sm text-muted-foreground">
                Nothing needs attention. Every source is current.
              </p>
            ) : (
              <>
                <p className="mb-2 text-xs text-muted-foreground">
                  {model.accountsNeedingAttention} account
                  {model.accountsNeedingAttention === 1 ? '' : 's'} affected.
                </p>
                <ul className="rounded-md border border-border px-3">
                  {model.attention.map((row) => (
                    <Row key={row.key} row={row} />
                  ))}
                </ul>
              </>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">Healthy</h2>
            <ul className="space-y-1 text-sm">
              {model.healthy.map((h) => (
                <li key={h.source} className="flex items-baseline gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${DOT.green}`} aria-hidden />
                  <span className="font-medium">{h.label}</span>
                  <span className="text-muted-foreground">
                    {h.note ??
                      `${h.healthyAccounts} of ${h.totalAccounts} accounts current`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </>
  );
}
